import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import pLimit from 'p-limit';
import { getUser, updateUser, createUser, getUserRoles } from 'api/users';
import Container from '@mui/material/Container';
import MKBox from 'components/MaterialKit/MKBox';
import { getErrorMessage, handleErrorResponse } from 'utils/general';
import { withLoading } from 'utils/hoc';
import { useProject } from 'contexts/project';
import { useAuth } from 'contexts/auth';
import { getLocaleMap } from 'utils/locales';
import { createUserCountry, deleteUserCountry, getCountries } from 'api/countries';
import { createUserFedexAccount, deleteUserFedexAccount, getFedexAccounts } from 'api/fedex';
import { createUserIWatchAccount, deleteUserIWatchAccount, getIWatchAccounts } from 'api/iwatch';
import EditUserForm from './EditUserForm';

const ResourceNotFound = () => {
  const [lm, setLm] = useState({});

  useEffect(() => {
    getLocaleMap(['labels_resource_not_found'])
      .then((response) => {
        setLm(response);
      });
  }, []);

  return (
    <MKBox flex={1} py="2rem">
      <Container>
        <h4>
          {lm.labels_resource_not_found}
        </h4>
      </Container>
    </MKBox>
  );
};

const EditUserSection = ({
  setLoading,
  title_color,
  title_font_size,
  title_font_weight,
  set_password_button_color,
  set_password_font_color,
  save_button_color,
  save_button_font_color,
  back_button_color,
  back_button_font_color,
}) => {
  const [lm, setLm] = useState({});

  useEffect(() => {
    getLocaleMap(['labels_edit', 'labels_create', 'labels_user'])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const [user, setUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [requestError, setRequestError] = useState(false);
  const [searchParams] = useSearchParams();
  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();
  const { project, setProject } = useProject();
  const userId = searchParams.get('username');
  const [countries, setCountries] = useState([]);
  const [fedexAccounts, setFedexAccounts] = useState([]);
  const [iWatchAccounts, setIWatchAccounts] = useState([]);

  const fetchCountries = useCallback(() => {
    return getCountries()
      .then(({ data }) => {
        setCountries(data);
      })
      .catch((error) => {
        handleErrorResponse(error, setAuth);
      });
  }, [setAuth, setCountries]);

  const fetchFedexAccounts = useCallback(() => {
    return getFedexAccounts()
      .then(({ data }) => {
        setFedexAccounts(data);
      })
      .catch((error) => {
        handleErrorResponse(error, setAuth);
      });
  }, [setAuth, setFedexAccounts]);

  const fetchIWatchAccounts = useCallback(() => {
    return getIWatchAccounts({
      active: true,
      $orderby: 'sequence'
    })
      .then(({ data }) => {
        setIWatchAccounts(data);
      })
      .catch((error) => {
        handleErrorResponse(error, setAuth);
      });
  }, [setAuth, setIWatchAccounts]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    fetchFedexAccounts();
  }, [fetchFedexAccounts]);

  useEffect(() => {
    fetchIWatchAccounts();
  }, [fetchIWatchAccounts]);

  const fetchUserRolesFromApi = useCallback(() => {
    return getUserRoles({ 'role_id[ne]': 3 })
      .then(({ data }) => {
        setUserRoles(data);
      })
      .catch((error) => {
        handleErrorResponse(error, setAuth);
      });
  }, [setAuth]);

  const fetchUserFromApi = useCallback(() => {
    if (!userId) {
      return Promise.resolve();
    }
    setLoading(true);
    return getUser(userId, {
      $expand: [
        // 'user_countries/country',
        'user_fedex_accounts/fedex_account',
        'user_iwatch_accounts/iwatch_account',
      ].join(),
    })
      .then(({ data }) => {
        setUser(data);
      })
      .catch((error) => {
        setRequestError(true);
        handleErrorResponse(error, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userId, setLoading, setAuth]);

  const updateUserToApi = useCallback((values, { setFieldError }) => {
    if (!userId) {
      return Promise.resolve();
    }

    const selectedCountryIds = values.selected_countries;
    const indexOfAllCountries = selectedCountryIds.indexOf('all');
    if (indexOfAllCountries > -1) { // only splice array when item is found
      selectedCountryIds.splice(indexOfAllCountries, 1);
    }
    selectedCountryIds.sort();

    const updateBody = {
      display_name: values.display_name,
      role: values.role,
      phone: values.phone,
      country_list: selectedCountryIds.join(','),
    };
    setLoading(true);

    // const userCountries = values.user_countries;
    // const prevCountryIds = values.initial_countries;
    // const newUserCountryIds = selectedCountryIds.filter((selectedId) => !prevCountryIds.includes(selectedId));
    // const deleteUserCountryIds = [];
    // prevCountryIds.forEach((prevId) => {
    //   if (!selectedCountryIds.includes(prevId)) {
    //     const targetUserCountry = userCountries.find((userCountry) => userCountry.country.country_id === prevId);
    //     deleteUserCountryIds.push(targetUserCountry.user_country_id);
    //   }
    // });

    const indexOfAllFedexAccounts = values.selected_fedex_accounts.indexOf('all');
    if (indexOfAllFedexAccounts > -1) { // only splice array when item is found
      values.selected_fedex_accounts.splice(indexOfAllFedexAccounts, 1);
    }
    const userFedexAccounts = values.user_fedex_accounts;
    const prevFedexAccountIds = values.initial_fedex_accounts;
    const selectedFedexAccountIds = values.selected_fedex_accounts;
    const newUserFedexAccountIds = selectedFedexAccountIds.filter((selectedId) => !prevFedexAccountIds.includes(selectedId));
    const deleteUserFedexAccountIds = [];
    prevFedexAccountIds.forEach((prevId) => {
      if (!selectedFedexAccountIds.includes(prevId)) {
        const targetUserFedexAccount = userFedexAccounts.find((fedexAccount) => fedexAccount.fedex_account.fedex_account_id === prevId);
        deleteUserFedexAccountIds.push(targetUserFedexAccount.user_fedex_account_id);
      }
    });

    const indexOfAllIWatchAccounts = values.selected_iwatch_accounts.indexOf('all');
    if (indexOfAllIWatchAccounts > -1) { // only splice array when item is found
      values.selected_iwatch_accounts.splice(indexOfAllIWatchAccounts, 1);
    }
    const userIwatchAccounts = values.user_iwatch_accounts;
    const prevIWatchAccountIds = values.initial_iwatch_accounts;
    const selectedIWatchAccountIds = values.selected_iwatch_accounts;
    const newUserIWatchAccountIds = selectedIWatchAccountIds.filter((selectedId) => !prevIWatchAccountIds.includes(selectedId));
    const deleteUserIWatchAccountIds = [];
    prevIWatchAccountIds.forEach((prevId) => {
      if (!selectedIWatchAccountIds.includes(prevId)) {
        const targetUserIWatchAccount = userIwatchAccounts.find((iWatchAccount) => iWatchAccount.iwatch_account.iwatch_account_id === prevId);
        deleteUserIWatchAccountIds.push(targetUserIWatchAccount.user_iwatch_account_id);
      }
    });

    const limit = pLimit(4);
    const requestPromises = [];

    // newUserCountryIds.forEach((newId) => {
    //   const postBody = {
    //     user: values.email,
    //     country: newId,
    //   };
    //   const requestPromise = limit(() => createUserCountry(postBody));
    //   requestPromises.push(requestPromise);
    // });
    // deleteUserCountryIds.forEach((deleteId) => {
    //   const requestPromise = limit(() => deleteUserCountry(deleteId));
    //   requestPromises.push(requestPromise);
    // });

    newUserFedexAccountIds.forEach((newId) => {
      const postBody = {
        user: values.email,
        fedex_account: newId,
      };
      const requestPromise = limit(() => createUserFedexAccount(postBody));
      requestPromises.push(requestPromise);
    });
    deleteUserFedexAccountIds.forEach((deleteId) => {
      const requestPromise = limit(() => deleteUserFedexAccount(deleteId));
      requestPromises.push(requestPromise);
    });

    newUserIWatchAccountIds.forEach((newId) => {
      const postBody = {
        user: values.email,
        iwatch_account: newId,
      };
      const requestPromise = limit(() => createUserIWatchAccount(postBody));
      requestPromises.push(requestPromise);
    });
    deleteUserIWatchAccountIds.forEach((deleteId) => {
      const requestPromise = limit(() => deleteUserIWatchAccount(deleteId));
      requestPromises.push(requestPromise);
    });

    return Promise.all(requestPromises)
      .then(() => {
        return updateUser(userId, updateBody)
          .then(({ data }) => {
            setUser(data);
            navigate(-1);
          })
          .catch((err) => {
            setFieldError('form', err.message);
          })
          .finally(() => {
            setLoading(false);
          });
      });
  }, [userId, setLoading, navigate]);

  const createUserToApi = useCallback((values, { setFieldError }) => {
    const selectedCountryIds = values.selected_countries;
    const indexOfAllCountries = selectedCountryIds.indexOf('all');
    if (indexOfAllCountries > -1) { // only splice array when item is found
      selectedCountryIds.splice(indexOfAllCountries, 1);
    }
    selectedCountryIds.sort();

    const createBody = {
      display_name: values.display_name,
      phone: values.phone,
      username: values.email,
      email: values.email,
      password: values.password,
      role: values.role,
      app: project?.app_id || auth?.user?.app,
      country_list: selectedCountryIds.join(','),
      // user_countries: selectedCountryIds.map(countryId => {
      //   return {
      //     country: countryId,
      //   };
      // }),
      user_fedex_accounts: values.selected_fedex_accounts.filter(fedexAccountId => fedexAccountId !== 'all').map(fedexAccountId => {
        return {
          fedex_account: fedexAccountId,
        };
      }),
      user_iwatch_accounts: values.selected_iwatch_accounts.filter(iWatchAccountId => iWatchAccountId !== 'all').map(iWatchAccountId => {
        return {
          iwatch_account: iWatchAccountId,
        };
      }),
    };
    setLoading(true);
    return createUser(createBody)
      .then(({ data }) => {
        return navigate(-1);
      })
    /*
      .then(({ data }) => {
        // navigate(-1);
        const newCountryIds = values.selected_countries;
        const userCountryPromises = newCountryIds.map((newId) => {
          const postBody = {
            user: values.email,
            country: newId,
          };
          return createUserCountry(postBody);
        });

        return Promise.all(userCountryPromises)
          .then(() => navigate(-1));
      })
    */
      .catch((err) => {
        setFieldError('form', getErrorMessage(err));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [auth, project?.app_id, setLoading, navigate]);

  useEffect(() => {
    fetchUserFromApi();
  }, [fetchUserFromApi]);

  useEffect(() => {
    fetchUserRolesFromApi();
  }, [fetchUserRolesFromApi]);

  if (requestError) {
    return <ResourceNotFound />;
  }

  return (
    <MKBox flex={1} py="2rem">
      <Container>
        <div style={{ marginBottom: 10, color: title_color, fontSize: title_font_size, fontWeight: title_font_weight }}>
          {`${userId ? lm.labels_edit : lm.labels_create} ${lm.labels_user}`}
        </div>
        <EditUserForm
          user={user}
          onSave={userId ? updateUserToApi : createUserToApi}
          roles={userRoles}
          countries={countries}
          fedexAccounts={fedexAccounts}
          iWatchAccounts={iWatchAccounts}
          setPasswordButtonColor={set_password_button_color}
          setPasswordFontColor={set_password_font_color}
          saveButtonColor={save_button_color}
          saveButtonFontColor={save_button_font_color}
          backButtonColor={back_button_color}
          backButtonFontColor={back_button_font_color}
        />
      </Container>
    </MKBox>
  );
};

EditUserSection.propTypes = {
  setLoading: PropTypes.func,
  title_color: PropTypes.string,
  title_font_size: PropTypes.string,
  title_font_weight: PropTypes.string,
  set_password_button_color: PropTypes.string,
  set_password_font_color: PropTypes.string,
  save_button_color: PropTypes.string,
  save_button_font_color: PropTypes.string,
  back_button_color: PropTypes.string,
  back_button_font_color: PropTypes.string,
};

export default withLoading(EditUserSection);
